/**
 * useContacts Hook
 * 문의 관리 관련 커스텀 훅
 */

import { useState, useCallback, useEffect } from 'react';
import { ContactRepository } from '../repositories/ContactRepository';
import { ContactService } from '../services/ContactService';
import { Contact, ContactStatus, ServiceType } from '../models';

const contactRepository = new ContactRepository();
const contactService = new ContactService(contactRepository);

export const useContacts = (serviceType?: ServiceType) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  /**
   * 모든 문의 조회
   */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await contactService.getAllContacts(serviceType);
      setContacts(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 대기 중인 문의 조회
   */
  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await contactService.getPendingContacts(serviceType);
      setContacts(data);
      setPendingCount(data.length);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 처리 중인 문의 조회
   */
  const fetchInProgress = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await contactService.getInProgressContacts(serviceType);
      setContacts(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 내가 맡은 문의 조회
   */
  const fetchMy = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await contactService.getMyContacts();
      setContacts(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 문의 배정
   */
  const assign = useCallback(
    async (contactId: string, assignToId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await contactService.assignContact({ contactId, assignToId });

        // 목록 새로고침
        await fetchPending();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '배정에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPending]
  );

  /**
   * 문의 응답
   */
  const respond = useCallback(
    async (contactId: string, response: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await contactService.respondToContact({ contactId, response });

        // 목록 새로고침
        await fetchMy();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '응답에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchMy]
  );

  /**
   * 문의 거부
   */
  const reject = useCallback(
    async (contactId: string, reason: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await contactService.rejectContact({ contactId, reason });

        // 목록 새로고침
        await fetchPending();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '거부에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPending]
  );

  /**
   * 문의 상태 변경
   */
  const updateStatus = useCallback(
    async (contactId: string, status: ContactStatus): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await contactService.updateContactStatus(contactId, status);

        // 현재 목록 새로고침
        await fetchAll();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : '상태 변경에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchAll]
  );

  /**
   * 대기 중인 문의 건수 갱신
   */
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await contactService.getPendingCount(serviceType);
      setPendingCount(count);
    } catch (err) {
      console.error('Failed to refresh pending count:', err);
    }
  }, [serviceType]);

  /**
   * 검색
   */
  const search = useCallback(
    (query: string) => {
      const filtered = contactService.searchContacts(contacts, query);
      setContacts(filtered);
    },
    [contacts]
  );

  // 마운트 시 대기 건수 조회
  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  return {
    contacts,
    pendingCount,
    loading,
    error,
    fetchAll,
    fetchPending,
    fetchInProgress,
    fetchMy,
    assign,
    respond,
    reject,
    updateStatus,
    refreshPendingCount,
    search,
  };
};
